import { useQuery } from "@tanstack/react-query";
import type { StakingRewardsInfo, StakingRewardsReaderFn } from "../types";

export const STAKING_ONCHAIN_QUERY_KEY = "wp:staking-onchain";

/**
 * 주입형 `stakingRewardsReader`로 연결 계정의 온체인 staking 값을 조회한다.
 * POSA delegation pool의 `earned()`(누적 미청구 리워드)와 `balanceOf()`(원금)를
 * raw wei BigInt로 돌려받아, posa 포트폴리오 "Accrued Rewards"와 동일한 수치를
 * 표기/계산하는 데 쓴다. reader 미주입 시 비활성화되고 undefined를 반환한다.
 *
 * queryKey에 walletAddress를 포함해 계정 전환 시 자동 갱신되게 한다.
 * (reader는 함수라 직렬화되지 않으므로 key에 넣지 않는다.)
 * `wp:` prefix 덕분에 포트폴리오 주기 새로고침에 함께 갱신된다.
 */
export function useStakingOnchain(
  walletAddress: string,
  reader: StakingRewardsReaderFn | undefined,
  enabled = true,
) {
  const isEnabled = enabled && !!reader && !!walletAddress;

  const { data, ...rest } = useQuery<StakingRewardsInfo>({
    queryKey: [STAKING_ONCHAIN_QUERY_KEY, walletAddress],
    queryFn: async () => {
      if (!reader) throw new Error("stakingRewardsReader not provided");
      return reader(walletAddress);
    },
    enabled: isEnabled,
    staleTime: 0,
  });

  return { onchain: data, ...rest };
}
